
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Upload, X, FileText, AlertTriangle } from 'lucide-react';
import { FormFile } from '@/hooks/useFormState';
import { toast } from 'sonner';
import { isFileTypeSupported, extractTextFromFile, ExtractedDocument } from '@/utils/documentParser';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface FileUploaderProps {
  onFileUploaded: (file: FormFile) => void;
  onFileRemoved: (fileName: string) => void;
  uploadedFiles: FormFile[];
  maxFileSize?: number; // in MB
  maxFiles?: number;
  showSupportingDocumentsInfo?: boolean;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  onFileUploaded,
  onFileRemoved,
  uploadedFiles,
  maxFileSize = 5, // Default changed to 5MB
  maxFiles = 5, // Default is 5 files
  showSupportingDocumentsInfo = false
}) => {
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const acceptedFileTypes = '.pdf,.doc,.docx,.txt';

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    
    if (!file) return;
    
    // Check if the maximum file limit has been reached
    if (uploadedFiles.length >= maxFiles) {
      toast.error(`You can only upload a maximum of ${maxFiles} files.`);
      return;
    }
    
    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxFileSize) {
      toast.error(`File size exceeds the maximum limit of ${maxFileSize}MB`);
      return;
    }
    
    // Check if file with same name already exists
    if (uploadedFiles.some(existingFile => existingFile.name === file.name)) {
      toast.error(`A file with the name "${file.name}" has already been uploaded.`);
      return;
    }
    
    // Validate file type
    if (!isFileTypeSupported(file)) {
      toast.error(`Unsupported file type. Only PDF, DOC, DOCX, and TXT files are accepted.`);
      return;
    }
    
    await processAndUploadFile(file);
    
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const processAndUploadFile = async (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      // Simulate upload progress for UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prevProgress => {
          const nextProgress = prevProgress + 5;
          if (nextProgress >= 50) {
            clearInterval(progressInterval);
            return 50; // Stop at 50% - the rest will be for text extraction
          }
          return nextProgress;
        });
      }, 100);
      
      setIsProcessing(true);
      
      // Extract text from the document
      let extractedDoc: ExtractedDocument;
      try {
        extractedDoc = await extractTextFromFile(file);
      } catch (error) {
        clearInterval(progressInterval);
        console.error("Text extraction error:", error);
        toast.error(`Failed to extract text from "${file.name}". ${error instanceof Error ? error.message : ''}`);
        setIsUploading(false);
        setIsProcessing(false);
        setUploadProgress(0);
        return;
      }
      
      // Update progress to show text extraction is complete
      setUploadProgress(85);
      
      // Create the FormFile object with the extracted text and original file metadata
      const fileData: FormFile = {
        name: file.name,
        type: file.type,
        size: file.size,
        file: file, // Keep original file for reference
        extractedText: extractedDoc.extractedText // Add extracted text
      };
      
      // Complete the progress
      setUploadProgress(100);
      onFileUploaded(fileData);
      toast.success(`File "${file.name}" processed successfully`);
    } catch (error) {
      console.error("File processing error:", error);
      toast.error(`Error processing file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      setIsProcessing(false);
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveFile = (fileName: string) => {
    onFileRemoved(fileName);
    toast.info(`File "${fileName}" removed`);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      {showSupportingDocumentsInfo && (
        <Alert className="bg-blue-50 border border-blue-200 mb-4">
          <AlertTriangle className="h-5 w-5 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <p className="font-medium">Upload your core supporting documents (max 5 files)</p>
            <p className="text-sm mt-1">You'll have the opportunity to upload more evidence related to specific discrimination types later in the form.</p>
          </AlertDescription>
        </Alert>
      )}
      
      <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 transition-colors hover:border-primary/50 bg-gray-50">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={acceptedFileTypes}
          className="hidden"
          disabled={uploadedFiles.length >= maxFiles || isUploading}
        />
        
        <div className="space-y-2 text-center">
          <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
          <div className="text-sm">
            <Button 
              type="button" 
              variant="ghost" 
              onClick={triggerFileInput}
              disabled={uploadedFiles.length >= maxFiles || isUploading}
            >
              Click to upload
            </Button>{' '}
            or drag and drop
          </div>
          <p className="text-xs text-muted-foreground">
            PDF, DOC, DOCX, TXT up to {maxFileSize}MB (max {maxFiles} files)
          </p>
          {uploadedFiles.length >= maxFiles && (
            <p className="text-xs text-amber-600 font-medium mt-1">
              Maximum number of files reached ({maxFiles}/{maxFiles})
            </p>
          )}
        </div>
      </div>

      {isUploading && (
        <div className="space-y-2">
          <Progress value={uploadProgress} className="h-2" />
          <p className="text-xs text-center text-muted-foreground">
            {isProcessing ? 'Extracting text...' : 'Uploading...'} {uploadProgress}%
          </p>
        </div>
      )}

      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Uploaded Documents ({uploadedFiles.length}/{maxFiles})</h4>
          <ul className="space-y-2">
            {uploadedFiles.map((file, index) => (
              <li key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-md border">
                <div className="flex items-center space-x-3">
                  <FileText className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium truncate max-w-[200px]">{file.name}</p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                    {file.extractedText && (
                      <p className="text-xs text-green-600">Text extracted</p>
                    )}
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => handleRemoveFile(file.name)}
                  className="h-8 w-8 rounded-full hover:bg-red-100 hover:text-red-500"
                >
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
